import type { TypePermission } from '@/features/domaine/types/opco'
import { useDialog } from '@/features/interface/dialog'
import { resolveEffectiveVisibility } from '@/router/visibility-helpers'
import type { RouteLocationNormalized, RouteLocationRaw, Router } from 'vue-router'
import { TypesContextesAutorisesEnum, type ContextAppType, type TypeContextCourant } from '../types'
import { useAppContextStore } from '../useAppContextStore'

export type ToggleContextResolver = (
  to: RouteLocationNormalized,
  contextCourant: TypeContextCourant,
  contextApp: ContextAppType,
) => RouteLocationRaw

declare module 'vue-router' {
  interface RouteMeta {
    /** L'accés à la page nécessite un contexte */
    requiresContext?: boolean
    toggleContextResolver?: ToggleContextResolver | string
    requirePermission?: TypePermission
    requiresAdmin?: boolean
  }
}

function routeParamToContext(param: string | string[] | undefined) {
  if (param === 'adherent') return TypesContextesAutorisesEnum.Adherent
  else if (param === 'prestataire') return TypesContextesAutorisesEnum.Prestataire
  else return TypesContextesAutorisesEnum.Aucun
}

function contextToRouteParam(contexte: TypeContextCourant) {
  return TypesContextesAutorisesEnum[contexte].toLowerCase()
}

export function registerContextGuard(router: Router) {
  router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
    if (to.meta.requiresContext !== true) return true

    const appContext = useAppContextStore()
    if (appContext.contextesAutorises === TypesContextesAutorisesEnum.Aucun) {
      const dialog = useDialog()
      await dialog.showDialog({
        title: 'Navigation refusée',
        message:
          'Vous devez rattacher au moins une entreprise ou établissement à votre compte utilisateur pour accéder à cette fonctionnalité.',
        type: 'Error',
        closable: true,
      })
      if (!from.name) return { name: 'home' }
      else return false
    }

    const routeContext = routeParamToContext(to.params?.context)

    // rewrite les routes par "défaut"
    if (routeContext === TypesContextesAutorisesEnum.Aucun) {
      to.params.context = contextToRouteParam(appContext.contextCourant)
      return to
    }

    // Test les droits de l'utilisateur au context
    if (!appContext.autoriseContext(routeContext)) {
      return { name: 'access-denied', query: { to: to.fullPath } }
    }

    // Modifie le contexte courant s'il est différent
    // du contexte de la route demandée
    if (routeContext !== appContext.contextCourant) {
      appContext.setContextCourant(routeContext)
    }

    // ⬇️ AJOUT: contrôle de visibilité (parent/enfant)
    const visibility = resolveEffectiveVisibility(to)
    if (visibility !== 'visible') {
      return from.name ? false : { name: 'home' }
      // return { name: 'access-denied', query: { to: to.fullPath } }
    }

    return true
  })
}

export function registerPermissionsGuard(router: Router) {
  router.beforeEach(async (to: RouteLocationNormalized, _from: RouteLocationNormalized) => {
    if (!to.meta.requirePermission) return true

    const appContext = useAppContextStore()
    const niveauPermission = appContext.permissions[to.meta.requirePermission]

    if (['Aucun', 'NC'].includes(niveauPermission)) {
      return { name: 'access-denied', query: { to: to.fullPath } }
    }

    return true
  })
}
